import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import {
  approvalSettingsService,
  CreateApprovalRuleRequest,
} from '../../services/purchaseOrderService';
import { roleService } from '../../services/roleService';
import { UserRole } from '../../types';
import '../../styles/ImsTheme.css';
import './ApprovalRuleCreatePage.css';

import { getServerErrorMessage } from '../../utils/errorUtils';
const EMPTY_FORM: CreateApprovalRuleRequest = {
  level: 1,
  ruleCategory: 'Purchase',
  ruleType: 'Role',
  minAmount: 0,
  maxAmount: undefined,
  requiredRoleId: undefined,
  description: '',
  isActive: true,
};

interface FieldErrors {
  level?: string;
  ruleCategory?: string;
  ruleType?: string;
  requiredRoleId?: string;
  minAmount?: string;
  maxAmount?: string;
}

const ApprovalRuleCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<CreateApprovalRuleRequest>(EMPTY_FORM);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    roleService.getRoles({ pageSize: 200 }).then(res => {
      const data = res.data?.data;
      setRoles(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked
        : name === 'level' || name === 'minAmount' ? Number(value)
        : name === 'maxAmount' ? (value === '' ? undefined : Number(value))
        : name === 'requiredRoleId' ? (value === '' ? undefined : Number(value))
        : value,
    }));
    setFieldErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.level || formData.level < 1 || formData.level > 10) {
      errors.level = t('approvalRule.levelRequired');
      isValid = false;
    }
    if (!formData.ruleCategory) {
      errors.ruleCategory = t('approvalRule.categoryRequired');
      isValid = false;
    }
    if (!formData.ruleType) {
      errors.ruleType = t('approvalRule.typeRequired');
      isValid = false;
    }
    if (!formData.requiredRoleId) {
      errors.requiredRoleId = t('approvalRule.roleRequired');
      isValid = false;
    }
    if (formData.minAmount < 0) {
      errors.minAmount = t('approvalRule.minAmountNegative');
      isValid = false;
    }
    if (formData.maxAmount != null && formData.maxAmount <= formData.minAmount) {
      errors.maxAmount = t('approvalRule.maxAmountInvalid');
      isValid = false;
    }

    setFieldErrors(errors);
    if (!isValid) setError(t('approvalRule.pleaseCorrectErrors'));
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    setLoading(true);
    try {
      await approvalSettingsService.createApprovalRule(formData);
      navigate('/settings/approval-rules', { state: { message: t('approvalRule.createdSuccess') } });
    } catch (axiosErr: unknown) {
      const err = axiosErr as { response?: { data?: { error?: { message?: string } } } };
      setError(getServerErrorMessage(err) || t('approvalRule.failedToCreate'));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">

        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/settings/approval-rules')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('approvalRule.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/settings/approval-rules')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            />
          </div>
        )}

        <div className="create-content">

          {/* Main Form */}
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('approvalRule.ruleDetails')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit}>

                  {/* Level / Category / Rule Type */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('approvalRule.level')} <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.level ? 'is-invalid' : ''}`}
                        name="level"
                        min={1}
                        max={10}
                        value={formData.level}
                        onChange={handleChange}
                      />
                      {fieldErrors.level
                        ? <small className="form-error">{fieldErrors.level}</small>
                        : <small className="form-helper">{t('approvalRule.levelHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('approvalRule.category')} <span className="required">*</span>
                      </label>
                      <select
                        className={`form-input form-select ${fieldErrors.ruleCategory ? 'is-invalid' : ''}`}
                        name="ruleCategory"
                        value={formData.ruleCategory}
                        onChange={handleChange}
                      >
                        <option value="Purchase">{t('approvalRule.categoryPurchase')}</option>
                        <option value="GRN">{t('approvalRule.categoryGRN')}</option>
                        <option value="Cancellation">{t('approvalRule.categoryCancellation')}</option>
                      </select>
                      {fieldErrors.ruleCategory && <small className="form-error">{fieldErrors.ruleCategory}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('approvalRule.ruleType')} <span className="required">*</span>
                      </label>
                      <select
                        className={`form-input form-select ${fieldErrors.ruleType ? 'is-invalid' : ''}`}
                        name="ruleType"
                        value={formData.ruleType}
                        onChange={handleChange}
                      >
                        <option value="Amount">{t('approvalRule.typeAmount')}</option>
                        <option value="Role">{t('approvalRule.typeRole')}</option>
                        <option value="Combined">{t('approvalRule.typeCombined')}</option>
                      </select>
                      {fieldErrors.ruleType
                        ? <small className="form-error">{fieldErrors.ruleType}</small>
                        : <small className="form-helper">
                            {formData.ruleType === 'Amount' && t('approvalRule.typeAmountHint')}
                            {formData.ruleType === 'Role' && t('approvalRule.typeRoleHint')}
                            {formData.ruleType === 'Combined' && t('approvalRule.typeCombinedHint')}
                          </small>}
                    </div>
                  </div>

                  {/* Required Role */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('approvalRule.requiredRole')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-input form-select ${fieldErrors.requiredRoleId ? 'is-invalid' : ''}`}
                      name="requiredRoleId"
                      value={formData.requiredRoleId ?? ''}
                      onChange={handleChange}
                    >
                      <option value="">{t('approvalRule.selectRole')}</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    {fieldErrors.requiredRoleId
                      ? <small className="form-error">{fieldErrors.requiredRoleId}</small>
                      : <small className="form-helper">{t('approvalRule.requiredRoleHint')}</small>}
                  </div>

                  {/* Min / Max Amount */}
                  <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="form-group">
                      <label className="form-label">{t('approvalRule.minAmount')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.minAmount ? 'is-invalid' : ''}`}
                        name="minAmount"
                        min={0}
                        step="0.01"
                        value={formData.minAmount}
                        onChange={handleChange}
                      />
                      {fieldErrors.minAmount && <small className="form-error">{fieldErrors.minAmount}</small>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('approvalRule.maxAmount')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.maxAmount ? 'is-invalid' : ''}`}
                        name="maxAmount"
                        min={0}
                        step="0.01"
                        value={formData.maxAmount ?? ''}
                        placeholder={t('approvalRule.unlimitedPlaceholder')}
                        onChange={handleChange}
                      />
                      {fieldErrors.maxAmount
                        ? <small className="form-error">{fieldErrors.maxAmount}</small>
                        : <small className="form-helper">{t('approvalRule.maxAmountHint')}</small>}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label className="form-label">{t('common.description')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="description"
                      value={formData.description ?? ''}
                      placeholder={t('approvalRule.descriptionPlaceholder')}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Active */}
                  <div className="arc-checkbox-row">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <label htmlFor="isActive">{t('approvalRule.isActive')}</label>
                  </div>


                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('common.info')}</h3>
              </div>
              <div className="create-card-body">
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p><strong>{t('approvalRule.howItWorksTitle')}:</strong></p>
                  <ul style={{ paddingLeft: '16px', margin: '8px 0' }}>
                    <li>{t('approvalRule.infoLevelUnique')}</li>
                    <li>{t('approvalRule.infoRoleRequired')}</li>
                    <li>{t('approvalRule.infoAmountOptional')}</li>
                    <li>{t('approvalRule.infoSequential')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default ApprovalRuleCreatePage;
